import { useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";

import { NewTabLink } from "components/NewTabLink";
import { useAPI, useShipmentRoute } from "hooks";
import { MODAL_STYLE } from "layouts/constants";
import {
  formatQuotingUnitOfMeasure,
  formatWeightLb,
  ISOStringToReadableDate,
} from "utils";

import {
  EVOTRUX_BLUE,
  SHIPMENT_TRACKING_LINK_BLOG_URL,
} from "../../../../constants";

interface Props {
  shipment: CondensedShipment | undefined;
  visible: boolean;
  onClose: VoidFunction;
  onBook: VoidFunction;
}

export function BookNowConfirmationModal(props: Props) {
  const { shipment, visible, onClose, onBook } = props;
  const { route, refetchRoute } = useShipmentRoute(shipment?.info.route);
  const shipmentRef = useRef<undefined | CondensedShipment>();
  const [canMeetReqs, setCanMeetReqs] = useState<boolean>(false);
  const [trackingLinkOffered, setTrackingLinkOffered] =
    useState<boolean>(false);
  const [userDeliveryEta, setUserDeliveryEta] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const { api } = useAPI();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (shipment && shipmentRef.current?.info?.id !== shipment.info.id) {
      shipmentRef.current = shipment;
      refetchRoute();
    }
  }, [shipment, refetchRoute]);

  const lastRoutePoint = route?.route_points?.length
    ? route.route_points[route.route_points.length - 1]
    : null;
  const isLastRouteASAP = lastRoutePoint?.schedule_type === "ASAP";
  const deliveryEta =
    userDeliveryEta ||
    (!isLastRouteASAP && lastRoutePoint ? lastRoutePoint.start_time : "");

  if (shipment === undefined) {
    return null;
  }

  const resetBookingModalState = () => {
    setCanMeetReqs(false);
    setNotes("");
  };

  const submitBookNow = () => {
    setLoading(true);
    setError(null);

    const deliveryDateTime = DateTime.fromISO(deliveryEta);

    const requestBody = {
      delivery_eta: deliveryDateTime,
      tracking_link_offered: trackingLinkOffered,
      notes: notes.trim() || null,
    };

    api<unknown>("post", `/shipments/${shipment.info.id}/book-now`, requestBody)
      .then(() => {
        setError(null);
        resetBookingModalState();
        onClose();
        /*
        Refetches the shipment, which remounts this subtree. Only on success:
        while it ran before the request, a rejected booking was unmounted
        before the catch below could render setError, so the carrier saw the
        modal vanish with no message.
        */
        onBook();
      })
      .catch((e) => {
        setError(
          e?.response?.data?.message ??
            "Failed to book shipment now. Please contact us if the issue persists.",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getEquipmentTypesDisplay = (equipmentTypes: Equipment[]) => {
    let equipmentTypesDisplay = "";
    equipmentTypes?.forEach((equipmentType, index) => {
      if (index === 0) {
        equipmentTypesDisplay = equipmentType.name;
      } else {
        equipmentTypesDisplay = `${equipmentTypesDisplay}, ${equipmentType.name}`;
      }
    });
    return equipmentTypesDisplay;
  };

  const onModalClose = () => {
    resetBookingModalState();
    onClose();
  };

  return (
    <Modal open={visible} onClose={onModalClose}>
      <Box sx={MODAL_STYLE}>
        <Stack direction="column">
          <Typography variant="h5">
            <b>{`Book this load now for $${shipment.book_now_info.book_now_price} ${shipment.book_now_info.book_now_currency}`}</b>{" "}
            <b>
              {shipment.info.quoting_unit_of_measure !== "ALL_IN"
                ? formatQuotingUnitOfMeasure(
                    shipment.info.quoting_unit_of_measure,
                    false,
                  )
                : ""}
            </b>
          </Typography>
          <Stack
            sx={{
              borderColor: EVOTRUX_BLUE,
              borderRadius: 2,
              p: "1rem",
              mt: "1rem",
              mb: "1rem",
            }}
            boxShadow={1}
            direction="column"
          >
            <Typography>
              <b>Service:</b> {shipment.info.service}
            </Typography>
            <Typography>
              <b>Weight:</b>{" "}
              {formatWeightLb(shipment.info.weight_lb, { includeKg: false })}
            </Typography>
            <Typography>
              <b>Pick up:</b>{" "}
              {ISOStringToReadableDate(shipment.first_point.start_time)}
            </Typography>
            {!isLastRouteASAP && (
              <Typography>
                <b>Deliver:</b>{" "}
                {ISOStringToReadableDate(shipment.last_point.start_time)}
              </Typography>
            )}
            <Typography>
              <b>Equipment:</b> {getEquipmentTypesDisplay(shipment.equipments)}
            </Typography>
            <Typography>
              <b>Special Requirements:</b>{" "}
              {shipment.info?.special_requirements ?? ""}
            </Typography>
            <Typography>
              <b>Product Dimensions:</b>{" "}
              {shipment.info?.additional_details ?? ""}
            </Typography>
          </Stack>
          {isLastRouteASAP && (
            <TextField
              id="datetime-local"
              label="Delivery ETA"
              type="date"
              value={deliveryEta}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => setUserDeliveryEta(e.target.value)}
              required
            />
          )}
          <TextField
            id="notes"
            label="Notes (optional)"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional details about your quote (e.g., fuel surcharge, special equipment, etc.)"
            inputProps={{ maxLength: 1024 }}
            helperText={`${notes.length}/1024 characters`}
            sx={{ mt: "1rem", width: "100%" }}
          />
          <Stack direction="row" mt="1rem" alignItems="center">
            <Checkbox
              checked={trackingLinkOffered}
              onChange={() => setTrackingLinkOffered(!trackingLinkOffered)}
            />
            <span>
              {"I can provide a tracking link for this shipment. "}
              <NewTabLink href={SHIPMENT_TRACKING_LINK_BLOG_URL}>
                <p>What are tracking links?</p>
              </NewTabLink>
            </span>
          </Stack>
          <Stack direction="row" alignItems="center" mt="1rem" mb="1rem">
            <Checkbox
              required
              checked={canMeetReqs}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setCanMeetReqs(event.target.checked)
              }
            />
            <Typography variant="h6" color="black">
              I confirm I will pick up this shipment with the requested
              equipments and can meet all service requirements listed above.
            </Typography>
          </Stack>
          {error !== null && (
            <Typography color="red" align="center">
              {error}
            </Typography>
          )}
          {!loading && (
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="success"
                disabled={
                  !canMeetReqs || (isLastRouteASAP && deliveryEta.length === 0)
                }
                onClick={submitBookNow}
              >
                {`BOOK NOW $${shipment.book_now_info.book_now_price} ${shipment.book_now_info.book_now_currency}`}{" "}
                {shipment.info.quoting_unit_of_measure !== "ALL_IN"
                  ? formatQuotingUnitOfMeasure(
                      shipment.info.quoting_unit_of_measure,
                      false,
                    )
                  : ""}
              </Button>
              <Button variant="outlined" onClick={onModalClose}>
                cancel
              </Button>
            </Stack>
          )}
          {loading && (
            <Stack>
              <CircularProgress />
            </Stack>
          )}
        </Stack>
      </Box>
    </Modal>
  );
}
